// ============================================================================
// MarketBalanceCard — баланс личного счёта мастера (BillingService.
// GetMyBillingAccount). Ошибки — явным текстом: выключенный биллинг/нет связи/
// права. Нули при ошибке не показываются (это была бы ложь о деньгах).
// ============================================================================

import { AlertTriangle, ChevronRight, RefreshCw, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import type { MasterBalanceResult } from '@/lib/marketFeed';
import { isBalanceFailure } from '@/lib/marketFeed';

interface MarketBalanceCardProps {
  state: MasterBalanceResult | null;
  loading: boolean;
  onRetry: () => void;
  /** Переход в кошелёк мастера (баланс, резервы, история, пополнение). */
  onOpenWallet?: () => void;
}

function money(value: number): string {
  return formatValue(value, 'number:0;group', 'numeric');
}

export function MarketBalanceCard({
  state,
  loading,
  onRetry,
  onOpenWallet,
}: MarketBalanceCardProps) {
  if (state && !isBalanceFailure(state)) {
    const b = state.balance;
    return (
      <section className="rounded-2xl border bg-card p-3.5 shadow-sm">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Wallet className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Баланс мастера
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xl font-semibold tabular-nums">{money(b.available)}</span>
              {b.currency && <span className="text-xs text-muted-foreground">{b.currency}</span>}
              <span className="text-[11px] text-muted-foreground">доступно</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 rounded-lg p-0"
            onClick={onRetry}
            disabled={loading}
            aria-label="Обновить баланс"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
          <span>На счёте: <span className="tabular-nums text-foreground">{money(b.balance)}</span></span>
          <span>В холдах: <span className="tabular-nums text-foreground">{money(b.holds)}</span></span>
          {onOpenWallet && (
            <button
              type="button"
              onClick={onOpenWallet}
              className="ml-auto inline-flex items-center gap-0.5 font-medium text-primary"
            >
              Кошелёк
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </section>
    );
  }

  if (!state) {
    return (
      <section className="rounded-2xl border bg-card p-3.5 shadow-sm">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
          Загружаем баланс мастера…
        </div>
      </section>
    );
  }

  // Сюда попадаем только с неуспешным состоянием (загрузка/успех — выше).
  const failed = isBalanceFailure(state) ? state : null;

  return (
    <section className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-3.5">
      <div className="flex items-start gap-2 text-xs leading-5 text-amber-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-medium">Баланс мастера недоступен</div>
          <div className="break-words">{failed?.message || 'Не удалось загрузить баланс'}</div>
          {failed?.kind === 'disabled' && (
            <div className="mt-1 text-[11px] text-amber-700">
              Включите биллинг в свойствах конфигурации — без него взятие заявок за плату невозможно.
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 rounded-lg p-0"
          onClick={onRetry}
          disabled={loading}
          aria-label="Повторить загрузку баланса"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
    </section>
  );
}
